import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import type { DefectAdminTreeNode, DefectSeverityFilter } from './defects.component';

@Component({
  selector: 'app-defect-admin-tree-node',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, DefectAdminTreeNodeComponent],
  templateUrl: './defect-admin-tree-node.component.html',
  styleUrl: './defect-admin-tree-node.component.css',
})
export class DefectAdminTreeNodeComponent {
  @Input({ required: true }) node!: DefectAdminTreeNode;
  @Input({ required: true }) expandedIds!: ReadonlySet<number>;
  @Input() focusedId: number | null = null;

  @Output() readonly nodeClick = new EventEmitter<DefectAdminTreeNode>();
  @Output() readonly focusChange = new EventEmitter<number>();

  readonly trackByChild = (_: number, child: DefectAdminTreeNode): number => child.defect.defectId;

  get isExpanded(): boolean {
    return this.expandedIds.has(this.node.defect.defectId);
  }

  get isFocused(): boolean {
    return this.focusedId === this.node.defect.defectId;
  }

  get severity(): Exclude<DefectSeverityFilter, 'all'> | null {
    const defect = this.node.defect;
    if (defect.critical) return 'critical';
    if (defect.major) return 'major';
    if (defect.minor) return 'minor';
    return null;
  }

  get title(): string {
    return this.node.defect.titleDE?.trim() || this.node.defect.titleEN || `#${this.node.defect.defectId}`;
  }

  get subtitle(): string {
    const titleEN = this.node.defect.titleEN?.trim();
    const titleDE = this.node.defect.titleDE?.trim();
    if (!titleEN || titleEN === titleDE) {
      return '';
    }
    return titleEN;
  }

  onClick(event: MouseEvent): void {
    event.stopPropagation();
    this.nodeClick.emit(this.node);
  }

  onFocus(): void {
    this.focusChange.emit(this.node.defect.defectId);
  }

  bubbleClick(node: DefectAdminTreeNode): void {
    this.nodeClick.emit(node);
  }

  bubbleFocus(id: number): void {
    this.focusChange.emit(id);
  }
}
